import {
  ADVANCED_NII_ACCOUNT_NAMES,
  CONCENTRATION,
  DENOMINATOR_AGGREGATION,
  GLOBAL,
  HR_DENOMINATOR,
  HR_NUMERATOR,
  NUMERATOR_AGGREGATION,
  TOTAL_LT_DEBT,
  TOTAL_LT_DEBT_AGGREGATION,
} from "./constants";
import customParseFormat from "dayjs/plugin/customParseFormat";
import dayjs from "dayjs";

dayjs.extend(customParseFormat);

export const minDate = dayjs("01-01-2000", "MM-DD-YYYY").startOf("month");
export const maxDate = dayjs().subtract(1, "month").endOf("month");

export const capitalizeFirstLetterOfEachWord = (sentence) => {
  if (!sentence) {
    return "";
  }
  return sentence
    .toLowerCase()
    .split(" ")
    .map((word) => {
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
};

export const extractAndSortFieldsFromCategories = (data) => {
  const fieldSet = new Set();
  (data || []).forEach((category) => {
    category?.tickers?.forEach((ticker) => {
      ticker?.fields?.forEach((field) => {
        fieldSet?.add(field);
      });
    });
  });

  const sortedFields = Array.from(fieldSet || []).sort();
  return sortedFields;
};

export const extractAndSortFieldsFromTickers = (data) => {
  const fieldSet = new Set();
  (data || []).forEach((ticker) => {
    ticker?.fields?.forEach((field) => {
      fieldSet?.add(field);
    });
  });

  const sortedFields = Array.from(fieldSet || []).sort();
  return sortedFields;
};

export const getRandomCSSColorCode = () => {
  const letters = "0123456789ABCDEF";
  let color = "#";
  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }
  return color;
};

export const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  return new Date(dateString).toLocaleString();
};

export const getSimpleFormattedDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Months are 0-indexed
  const day = date.getDate().toString().padStart(2, "0");
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const seconds = date.getSeconds().toString().padStart(2, "0");

  // Example format: YYYYMMDD_HHMMSS
  return `${year}${month}${day}_${hours}${minutes}${seconds}`;
};

export const getIsoStringToDate = (isoString) => {
  const date = new Date(isoString.split(".")[0]);

  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  };

  const formatted = date.toLocaleString("en-US", options);

  const [month, day, yearAndTime] = formatted.split("/");
  const [year, time] = yearAndTime.split(", ");

  const result = `${month.padStart(2, "0")}-${day.padStart(2, "0")}-${year} ${time}`;

  return result;
};

export const calculateDividedDatas = (datas, divisor, decimalPlaces) => {
  const entries = Object.entries(datas);
  const dividedEntries = entries.map(([key, value]) => {
    const dividedValue = value / divisor;
    const formattedValue = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }).format(dividedValue);
    return [key, formattedValue];
  });

  return Object.fromEntries(dividedEntries);
};

export const parseNumber = (str) => {
  if (typeof str !== "string") return str;
  return Number(str?.replace(/,/g, ""));
};

export const formatNumber = (num) => {
  return num.toLocaleString("en-US");
};

export const getPercentage = (sumOfNumerators, sumOfDenominators) => {
  const numerator = parseNumber(sumOfNumerators);
  const denominator = parseNumber(sumOfDenominators);

  if (!denominator) return 0;
  const rawPercentage = (numerator / denominator) * 100;
  const MAX_DECIMALS = 2;
  const finalRoundedPercentage = parseFloat(
    rawPercentage.toFixed(MAX_DECIMALS),
  );
  return finalRoundedPercentage;
};

export const prepareDataForHedgeRatio = (
  dataArray,
  topParentId,
  type,
  showChildren,
) => {
  if (!dataArray || dataArray.length === 0) {
    return [];
  }

  const getAggregationValue = () => {
    if (type === HR_NUMERATOR) return NUMERATOR_AGGREGATION;
    if (type === HR_DENOMINATOR) return DENOMINATOR_AGGREGATION;
    return "NA";
  };

  const getCellStyles = (reportingAccountName) => {
    const styles = {};

    if (
      reportingAccountName === HR_NUMERATOR ||
      reportingAccountName === HR_DENOMINATOR
    ) {
      styles.fontSize = "13px";
      styles.fontWeight = "bold";
      styles.backgroundColor = "#F5F5F5";
      styles.borderTop = "1px solid #000000";
      styles.borderBottom = "1px solid #000000";
    }
    return styles;
  };

  return (dataArray || []).map((item) => ({
    ...item,
    id: item.accountNum,
    reportingAccountName: item.reportingAccountName,
    aggregation: getAggregationValue(),
    cellStyles: getCellStyles(item?.reportingAccountName),
    ...item.datas,
    children:
      showChildren && item.subAccounts
        ? prepareDataForHedgeRatio(item?.subAccounts, topParentId, type, true)
        : [],
  }));
};

export const flattenHRAccountData = (nodes, aggregation, accumulator = []) => {
  if (!nodes || nodes.length === 0) {
    return accumulator;
  }

  for (const node of nodes) {
    if (node?.accountNum !== null) {
      const simplifiedNode = {
        accountNum: node?.accountNum,
        reportingAccountName: node?.reportingAccountName,
        formattedReportingAccountName: node?.formattedReportingAccountName,
        aggregation: aggregation,
        level: node?.level,
        ...node?.datas,
      };
      accumulator.push(simplifiedNode);
    }

    if (node?.subAccounts && node?.subAccounts.length > 0) {
      flattenHRAccountData(node?.subAccounts, aggregation, accumulator);
    }
  }

  return accumulator;
};

export const applyFigureFormatting = (
  value,
  selectedFigureDivisor,
  selectedDecimalsIn,
) => {
  let numericValue = Number(value);
  if (isNaN(numericValue)) return value;

  const divisor = parseFloat(selectedFigureDivisor) || 1;
  const shouldDivide = divisor !== 1;

  const decimals =
    selectedDecimalsIn !== null && selectedDecimalsIn !== undefined
      ? parseInt(selectedDecimalsIn, 10)
      : null;
  const shouldFormatDecimals = decimals !== null && !isNaN(decimals);

  if (shouldDivide) {
    numericValue = numericValue / divisor;
  }

  if (shouldFormatDecimals) {
    return numericValue.toFixed(decimals);
  }

  return numericValue;
};

export const prepareDataForMarketValue = (accounts, netEquityBalance = 0) => {
  return accounts?.map((account) => {
    const { subAccounts, datas, accountNum, ...rest } = account || {};

    const percent = (numerator, denominator) =>
      denominator !== 0
        ? ((numerator / denominator) * 100).toFixed(2) + "%"
        : "-";

    return {
      id: accountNum,
      accountNum,
      ...rest,
      ...datas,
      PercentageDV100SensitivityOverBalance: percent(
        Number(datas?.DV100Sensitivity),
        Math.abs(Number(datas?.Balance)),
      ),
      PercentageDV100OverMktValue: percent(
        Number(datas?.DV100Sensitivity),
        Math.abs(Number(datas?.MarketValue)),
      ),
      PercentageDV100ContributionToEquity: percent(
        Number(datas?.DV100Sensitivity),
        Math.abs(Number(netEquityBalance)),
      ),
      datas,
      children:
        subAccounts && subAccounts.length > 0
          ? prepareDataForMarketValue(subAccounts, netEquityBalance)
          : [],
    };
  });
};

export const prepareDataForBalanceSheet = (
  dataArray,
  {
    divisor = 1,
    decimals = null,
    showChildren = true,
    formatValues = true,
  } = {},
) => {
  if (!dataArray || dataArray.length === 0) {
    return [];
  }

  const getAggregationValue = (item) => {
    if (item?.level === "Level 0") return "ROOT";
    return "NA";
  };

  const getCellStyles = (item) => {
    const styles = {};

    if (item?.level === "Level 0") {
      styles.fontSize = "13px";
    }

    return styles;
  };

  return (dataArray || []).map((item) => {
    const formattedDatas = {};

    for (const [key, value] of Object.entries(item.datas || {})) {
      let finalValue = Number(value);

      if (formatValues) {
        finalValue = finalValue / divisor;

        if (decimals !== null && decimals !== undefined) {
          finalValue = finalValue.toFixed(decimals);
        }
      }

      formattedDatas[key] = finalValue;
    }

    return {
      ...item,
      id: item.accountNum,
      reportingAccountName: item.reportingAccountName,
      aggregation: getAggregationValue(item),
      cellStyles: getCellStyles(item),
      datas: formattedDatas,
      ...formattedDatas,
      children:
        showChildren && item.subAccounts && item.subAccounts.length > 0
          ? prepareDataForBalanceSheet(item.subAccounts, {
              divisor,
              decimals,
              showChildren: true,
              formatValues,
            })
          : [],
    };
  });
};

export const flattenBalanceSheetData = (nodes, accumulator = []) => {
  if (!nodes || nodes.length === 0) {
    return accumulator;
  }

  for (const node of nodes) {
    if (node?.accountNum !== null && node?.accountNum !== undefined) {
      const simplifiedNode = {
        accountNum: node?.accountNum,
        reportingAccountName: node?.reportingAccountName,
        formattedReportingAccountName: node?.formattedReportingAccountName,
        aggregation: node?.aggregation,
        level: node?.level,
        ...node?.datas,
      };

      accumulator.push(simplifiedNode);
    }

    if (node?.subAccounts && node?.subAccounts.length > 0) {
      flattenBalanceSheetData(node.subAccounts, accumulator);
    }
  }

  return accumulator;
};

export const sliceObjectByIndex = (obj, startIndex, limit) => {
  const entries = Object.entries(obj); // array of [key, value]
  const slicedEntries = entries.slice(startIndex, startIndex + limit);
  return Object.fromEntries(slicedEntries);
};

export const prepareDataForLTDC = (
  dataArray,
  topParentId,
  type,
  showChildren,
) => {
  if (!dataArray || dataArray.length === 0) {
    return [];
  }

  const getAggregationValue = () => {
    if (type === TOTAL_LT_DEBT) return TOTAL_LT_DEBT_AGGREGATION;
    return "NA";
  };

  const getCellStyles = (reportingAccountName) => {
    const styles = {};

    if (
      reportingAccountName === TOTAL_LT_DEBT ||
      reportingAccountName === CONCENTRATION
    ) {
      styles.fontSize = "13px";
      styles.fontWeight = "bold";
      styles.backgroundColor = "#F5F5F5";
      styles.borderTop = "1px solid #000000";
      styles.borderBottom = "1px solid #000000";
    }
    return styles;
  };

  const formatConcentrationData = (datas) => {
    if (!datas) return {};

    return Object.fromEntries(
      Object.entries(datas).map(([key, value]) => [
        key,
        value !== null && value !== undefined ? `${value}%` : value,
      ]),
    );
  };

  return (dataArray || []).map((item) => ({
    ...item,
    id: item.accountNum,
    reportingAccountName: item.reportingAccountName,
    aggregation: getAggregationValue(),
    cellStyles: getCellStyles(item?.reportingAccountName),
    ...(item.reportingAccountName === CONCENTRATION
      ? formatConcentrationData(item.datas)
      : item.datas),
    children:
      showChildren && item.subAccounts
        ? prepareDataForHedgeRatio(item?.subAccounts, topParentId, type, true)
        : [],
  }));
};

export const calculateConcentration = (dataArray) => {
  const totalLTDebtObj = dataArray?.find(
    (item) => item.reportingAccountName === TOTAL_LT_DEBT,
  );

  if (!totalLTDebtObj) {
    throw new Error(TOTAL_LT_DEBT + " account not found");
  }

  const datas = totalLTDebtObj?.datas;

  const totalSum = Object.values(datas)?.reduce((acc, val) => acc + val, 0);

  const concentrationDatas = {};
  for (const [date, value] of Object.entries(datas)) {
    concentrationDatas[date] =
      totalSum === 0 ? 0 : ((value / totalSum) * 100)?.toFixed(2);
  }

  return {
    accountNum: 0,
    reportingAccountName: CONCENTRATION,
    formattedReportingAccountName: CONCENTRATION,
    level: null,
    datas: concentrationDatas,
    subAccounts: [],
  };
};

export const getMaxConcentration = (dataArray) => {
  const totalLTDebtObj = (dataArray || []).find(
    (item) => item.reportingAccountName === TOTAL_LT_DEBT,
  );

  if (!totalLTDebtObj) {
    throw new Error(TOTAL_LT_DEBT + " account not found");
  }

  const datas = totalLTDebtObj.datas;

  const totalSum = Object.values(datas).reduce((acc, val) => acc + val, 0);
  if (totalSum === 0) return 0;

  let maxKey = null;
  let maxPercentage = -Infinity;

  for (const [key, value] of Object.entries(datas)) {
    const percentage = (value / totalSum) * 100;
    if (percentage > maxPercentage) {
      maxPercentage = percentage;
      maxKey = key;
    }
  }

  return {
    maxMonth: maxKey,
    maxMonthTotalLTDebt: datas[maxKey],
    maxConcentration: parseFloat(maxPercentage.toFixed(2)),
  };
};

// Icon components
export const NewsIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="#FFD700"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginRight: "0.4rem", verticalAlign: "middle" }}
  >
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10c5.52 0 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
  </svg>
);

export const UpdateIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="#00BFFF"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginRight: "0.4rem", verticalAlign: "middle" }}
  >
    <path d="M12 22c1.1 0 2-.9 2-2h-4a2 2 0 002 2zm6-6v-5c0-3.07-1.63-5.64-4.5-6.32V4a1.5 1.5 0 00-3 0v.68C7.63 5.36 6 7.92 6 11v5l-1.99 2H20l-2-2z" />
  </svg>
);

export const AlertIcon = () => (
  <svg
    aria-hidden="true"
    focusable="false"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="#FF4500"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginRight: "0.4rem", verticalAlign: "middle" }}
  >
    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
  </svg>
);

export const hasAnyOneBusinessEntityIDInNonGlobalRegions = (
  regions = [],
  businessEntityIDsToCheck = [],
  checkWritable = false,
) => {
  return regions?.some(
    (region) =>
      region?.regionName?.toLowerCase() !== GLOBAL &&
      region?.sfcs?.some(
        (sfc) =>
          businessEntityIDsToCheck?.includes(sfc?.businessEntityID) &&
          (!checkWritable || sfc?.writable === true),
      ),
  );
};

export const getMappedBusinessEntityIDs = (hrFiltersData, regions) => {
  const filteredSfcs = [
    ...new Map(
      (hrFiltersData || []).map((item) => [item?.modelEntity, item]),
    ).values(),
  ].sort((a, b) => (a?.modelEntity || "").localeCompare(b?.modelEntity || ""));

  const allsfcs = (regions || [])
    .flatMap((region) => region?.sfcs || [])
    .sort((a, b) =>
      (a?.businessEntityID || "").localeCompare(b?.businessEntityID || ""),
    );

  const useFiltered = filteredSfcs.length > 0;
  const source = useFiltered ? filteredSfcs : allsfcs;

  return source.map((item) =>
    useFiltered
      ? { id: item?.modelEntity, name: item?.modelEntity }
      : { id: item?.businessEntityID, name: item?.businessEntityID },
  );
};

export const getAllowedMonthEnds = (selectedModelEntity, data) => {
  const matchingModels = data.filter(
    (item) => item.modelEntity === selectedModelEntity,
  );

  const monthEnds = matchingModels
    .flatMap((item) => item.dateModels || [])
    .map((dateModel) => dateModel.monthEnd);

  return [...new Set(monthEnds)];
};

export const getDisplayReportingAccountName = (name) => {
  if (name === ADVANCED_NII_ACCOUNT_NAMES.TOTAL_RECEIVABLES) {
    return `${ADVANCED_NII_ACCOUNT_NAMES.TOTAL_RECEIVABLES} (Assets)`;
  }

  if (name === ADVANCED_NII_ACCOUNT_NAMES.TOTAL_PAYABLES) {
    return `${ADVANCED_NII_ACCOUNT_NAMES.TOTAL_PAYABLES} (Liabilities)`;
  }

  return name;
};

export const applyFilterForValues = (
  value,
  selectedFigureDivisor,
  selectedDecimalsIn,
) => {
  if (typeof value !== "number") {
    return null;
  }

  const divisor = parseFloat(selectedFigureDivisor) || 1;
  const shouldDivide = divisor !== 1;

  const decimals =
    selectedDecimalsIn !== null && selectedDecimalsIn !== undefined
      ? parseInt(selectedDecimalsIn, 10)
      : null;
  const shouldFormatDecimals = decimals !== null && !isNaN(decimals);

  let result = value;

  if (shouldDivide) {
    result = result / divisor;
  }

  if (shouldFormatDecimals) {
    return result.toFixed(decimals);
  }

  return Number(result);
};
